/**
 * SEO 结构化数据工具
 * 管理 JSON-LD 的动态注入与移除
 *
 * 支持的结构化数据类型：
 * - Organization  站点组织信息（全局常驻）
 * - WebApplication 计算器工具应用信息（每页切换）
 * - FAQPage        从 seo_sections 中提取问答对
 */

const SITE_ORIGIN = 'https://www.jianqiyue.com';

// JSON-LD 标签的统一标记，方便查找和清理
const JSONLD_MARK = 'data-seo-jsonld';
// 当前页面级 JSON-LD 的标记（排除全局的 Organization）
const PAGE_MARK = 'data-seo-page';

/**
 * 站点级 Organization 结构化数据（常驻 head，不随路由变化）
 */
const ORGANIZATION_DATA = {
	'@context': 'https://schema.org',
	'@type': 'Organization',
	name: '建数工厂',
	url: SITE_ORIGIN,
	logo: SITE_ORIGIN + '/favicon.ico',
	description: '建筑工程专业工具平台，提供20+免费工程费用计算器、全国招标信息查询、建筑企业资质检索。',
	contactPoint: {
		'@type': 'ContactPoint',
		telephone: '+86-010-88210079',
		contactType: 'customer service',
		areaServed: 'CN',
		availableLanguage: ['Chinese'],
	},
	address: {
		'@type': 'PostalAddress',
		addressCountry: 'CN',
		addressLocality: '北京市海淀区西翠路9号',
	},
};

/**
 * 从 CALC_REGISTRY 条目构建 WebApplication JSON-LD
 * @param {Object} calc 注册表中的计算器配置
 * @returns {Object|null}
 */
export function buildWebApplicationJsonLd(calc) {
	if (!calc) return null;

	return {
		'@context': 'https://schema.org',
		'@type': 'WebApplication',
		name: calc.name,
		url: SITE_ORIGIN + calc.path,
		description: calc.seo?.description || calc.name,
		applicationCategory: 'BusinessApplication',
		operatingSystem: 'Web Browser',
		browserRequirements: 'Requires JavaScript',
		offers: {
			'@type': 'Offer',
			price: '0',
			priceCurrency: 'CNY',
			availability: 'https://schema.org/InStock',
		},
		publisher: {
			'@type': 'Organization',
			name: '建数工厂',
			url: SITE_ORIGIN,
		},
	};
}

/**
 * 从 seo_sections 中提取问答对，生成 FAQPage JSON-LD
 * 仅提取标题包含"？"的段落作为问答对
 * @param {Array} seoSections 计算器 schema 中的 seo_sections
 * @returns {Object|null}
 */
export function buildFaqPageJsonLd(seoSections) {
	if (!seoSections || !Array.isArray(seoSections) || seoSections.length === 0) return null;

	const faqEntries = seoSections
		.filter(sec => sec.title && sec.title.includes('？'))
		.map(sec => ({
			'@type': 'Question',
			name: sec.title,
			acceptedAnswer: {
				'@type': 'Answer',
				// 去除 HTML 标签，保留纯文本
				text: stripHtml(sec.content || ''),
			},
		}));

	if (faqEntries.length === 0) return null;

	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqEntries,
	};
}

/**
 * 从 CALC_REGISTRY 条目构建 BreadcrumbList JSON-LD
 * @param {Object} calc 注册表中的计算器配置
 * @returns {Object}
 */
export function buildBreadcrumbJsonLd(calc) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: '首页',
				item: SITE_ORIGIN,
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: '工程计算器',
				item: SITE_ORIGIN + '/CalcPage',
			},
			{
				'@type': 'ListItem',
				position: 3,
				name: calc.name,
				item: SITE_ORIGIN + calc.path,
			},
		],
	};
}

/**
 * 移除上一个页面注入的 JSON-LD 标签（保留全局 Organization）
 */
export function clearPageJsonLd() {
	const scripts = document.querySelectorAll(`script[${PAGE_MARK}]`);
	scripts.forEach(s => s.remove());
}

/**
 * 注入单个 JSON-LD 脚本标签
 * @param {Object} data  JSON-LD 数据对象
 * @param {string} tag    标记名称（用于标识）
 */
function injectJsonLd(data, tag) {
	const script = document.createElement('script');
	script.type = 'application/ld+json';
	script.setAttribute(JSONLD_MARK, '');
	script.setAttribute(PAGE_MARK, tag);
	script.textContent = JSON.stringify(data);
	document.head.appendChild(script);
}

/**
 * 为计算器页面注入结构化数据
 * @param {Object} calc  注册表中的计算器配置
 */
export function injectCalcJsonLd(calc) {
	// 先清理上一页的
	clearPageJsonLd();

	if (!calc) return;

	// WebApplication
	const webApp = buildWebApplicationJsonLd(calc);
	if (webApp) injectJsonLd(webApp, 'webapp');

	// FAQPage（从 seo_sections 提取问答）
	const seoSections = calc.schema?.seo_sections;
	const faq = buildFaqPageJsonLd(seoSections);
	if (faq) injectJsonLd(faq, 'faq');

	// BreadcrumbList
	const breadcrumb = buildBreadcrumbJsonLd(calc);
	injectJsonLd(breadcrumb, 'breadcrumb');
}

/**
 * 初始化全局 Organization JSON-LD（仅在应用启动时调用一次）
 */
export function initGlobalJsonLd() {
	// 如果已存在则跳过
	if (document.querySelector(`script[${JSONLD_MARK}][${PAGE_MARK}="organization"]`)) return;
	const script = document.createElement('script');
	script.type = 'application/ld+json';
	script.setAttribute(JSONLD_MARK, '');
	script.setAttribute(PAGE_MARK, 'organization');
	script.textContent = JSON.stringify(ORGANIZATION_DATA);
	document.head.appendChild(script);
}

/**
 * 简易 HTML 标签清除
 */
function stripHtml(html) {
	const tmp = document.createElement('div');
	tmp.innerHTML = html;
	return tmp.textContent || tmp.innerText || '';
}
